// Central content for Hussar Marine Concierge (Phase 1).
// Copy is intentionally compliant: no insurance/USCG/certification claims,
// no marina partnerships, no fabricated testimonials or client counts.

export const BRAND = {
  name: "Hussar Marine Concierge",
  short: "HMC",
  parent: "Hussar Holdings LLC",
  tagline: "Your Vessel. Our Vigilance.",
  domain: "HussarMarineConcierge.com",
  email: "contact@hussarholdings.pl",
  serviceArea: "Southwest Florida",
};

export const REGIONS = [
  "Naples",
  "Marco Island",
  "Fort Myers Beach",
  "Cape Coral",
  "Sanibel & Captiva",
  "Punta Gorda / Charlotte Harbor",
];

export const DIFFERENTIATORS = [
  {
    icon: "Eye",
    title: "Scheduled Dockside Inspections",
    text: "Routine visual condition checks at your vessel's slip — documented with timestamped photographs on every visit.",
  },
  {
    icon: "BatteryCharging",
    title: "Systems & Bilge Observation",
    text: "Visual verification of shore power, battery condition indicators, and bilge activity, with any concern flagged promptly.",
  },
  {
    icon: "CloudLightning",
    title: "Storm-Readiness Reporting",
    text: "Pre-storm line, fender and cover checks plus post-weather condition reporting so you know how your vessel fared.",
  },
  {
    icon: "Headset",
    title: "Contractor Coordination",
    text: "We coordinate and provide dockside access for the licensed marine contractors you choose — on your schedule.",
  },
];

export const SERVICES = [
  {
    slug: "vessel-watch",
    name: "Vessel Watch",
    icon: "Eye",
    tagline: "Regular eyes on your vessel while you're away.",
    summary:
      "Scheduled dockside visual inspections for seasonal and absentee owners who want consistent, documented peace of mind.",
    hero: "https://images.unsplash.com/photo-1631655104117-f1cfdf688772?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    includes: [
      "Scheduled dockside visual inspection visits",
      "Waterline, dock lines & fender condition checks",
      "Exterior hull, canvas and cover visual review",
      "Timestamped photographs filed with each visit",
      "Written condition summary delivered by email",
      "Optional pre-storm visual check on request",
    ],
    note:
      "Vessel Watch is a visual condition-monitoring service. It does not include mechanical repair, certification, or vessel operation.",
  },
  {
    slug: "vessel-oversight",
    name: "Vessel Oversight",
    icon: "ShieldCheck",
    tagline: "Comprehensive, documented oversight of key systems.",
    summary:
      "A more thorough, more frequent oversight program covering visible systems indicators, with priority storm reporting.",
    hero: "https://images.unsplash.com/photo-1544119154-ef19cd315b7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    includes: [
      "More frequent, comprehensive dockside inspections",
      "Battery indicator & bilge pump activity observation",
      "Shore power connection & visible HVAC status check",
      "High-resolution photo and short video condition reports",
      "Priority pre- and post-storm inspection reporting",
      "Standardized inspection checklist on every visit",
    ],
    note:
      "Observations are visual and informational. Any repair or service is coordinated through licensed third-party marine contractors you approve.",
  },
  {
    slug: "concierge",
    name: "Concierge Services",
    icon: "Compass",
    tagline: "White-glove coordination around your vessel.",
    summary:
      "On-demand coordination and dockside supervision — from contractor access to pre-arrival staging — handled discreetly.",
    hero: "https://images.unsplash.com/photo-1745167554647-a02b5011140b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    includes: [
      "Dockside supervision for approved contractor visits",
      "Scheduling & coordination of detailing and wash services",
      "Pre-arrival staging checks (A/C, lighting, cleanliness)",
      "Provisioning coordination with your chosen vendors",
      "Single point of contact for vessel-related logistics",
      "Custom checklist tailored to your vessel",
    ],
    note:
      "Concierge coordination arranges services performed by independent, licensed providers. HMC does not operate vessels or perform mechanical work.",
  },
];

export const TIERS = [
  {
    id: "anchor-sentinel",
    name: "Anchor Sentinel",
    price: 299,
    popular: false,
    description: "Essential dockside oversight for seasonal owners who want regular, documented peace of mind.",
    features: [
      "Bi-weekly (2x / month) dockside visual inspection",
      "Waterline, lines & fender condition checks",
      "Exterior hull & canvas inspection report",
      "Timestamped photos & checklist by email",
      "Severe-weather alert & pre-storm check option",
    ],
    testid: "pricing-tier-anchor-sentinel",
  },
  {
    id: "captains-vigilance",
    name: "Premier Vigilance",
    price: 499,
    popular: true,
    badge: "Most Popular",
    description: "Comprehensive weekly oversight and visible-systems monitoring for higher-value vessels.",
    features: [
      "Weekly (4x / month) dockside vessel inspection",
      "Battery indicator & bilge pump activity checks",
      "Shore power & visible systems status verification",
      "High-definition photo & video status reports",
      "Priority pre- & post-storm inspection reporting",
      "Dockside contractor escort (up to 1 hr / month)",
    ],
    testid: "pricing-tier-captains-vigilance",
  },
  {
    id: "admiralty-suite",
    name: "Admiralty Suite",
    price: 799,
    popular: false,
    description: "White-glove concierge and full dockside guardianship for motor yachts and luxury vessels.",
    features: [
      "Weekly comprehensive inspection + custom checklist",
      "Full exterior, canvas & cover condition review",
      "Shore power & visible dockside systems check",
      "Contractor dispatch coordination & dock supervision",
      "Pre-arrival exterior staging & wash-check coordination",
      "Dedicated Marine Concierge Specialist contact",
    ],
    testid: "pricing-tier-admiralty-suite",
  },
];

export const VESSEL_TYPES = [
  "Motor Yacht",
  "Sport Fishing / Center Console",
  "Cruiser / Express",
  "Sailboat",
  "Catamaran",
  "Other",
];

export const FREQUENCIES = [
  "Bi-weekly (2x / month)",
  "Weekly (4x / month)",
  "Custom schedule",
  "Not sure — advise me",
];

export const FAQS = [
  {
    q: "What exactly does a dockside inspection include?",
    a: "A scheduled visual condition check of your vessel at its slip — exterior, lines, fenders, covers, and visible systems indicators — documented with timestamped photographs and a written summary.",
  },
  {
    q: "Do you perform repairs or mechanical work?",
    a: "No. HMC provides visual oversight and reporting. When a repair or service is needed, we coordinate access for the licensed, independent marine contractors you approve.",
  },
  {
    q: "Which areas do you serve?",
    a: "Southwest Florida, including Naples, Marco Island, Fort Myers Beach, Cape Coral, Sanibel & Captiva, and the Punta Gorda / Charlotte Harbor area.",
  },
  {
    q: "How do I get started?",
    a: "Submit an inspection request with your vessel details and marina location. We'll follow up to confirm scope, scheduling, and the plan that fits your needs.",
  },
];

export const DISCLAIMER =
  "Hussar Marine Concierge is an independent vessel oversight and dockside monitoring service, currently operated by Hussar Holdings LLC (Florida). A dedicated operating entity, Hussar Marine Concierge LLC, is intended to be formed as a wholly owned subsidiary of Hussar Holdings LLC; it has not yet been formed, and references to it describe an intended future structure only. HMC coordinates third-party specialized marine services upon client request. We do not operate vessels, perform mechanical certification, or guarantee protection against storm damage, and we do not imply affiliation with any marina without written authorization. Monthly subscription payments are processed securely by Stripe; HMC does not store card details.";
